/**
 * Heroic Commitment Agreement, version 1.
 *
 * Per ONB-020 + the System Map's Step 0 Discovery Call, the client signs this
 * commitment before they enter the program. The full text is stored as the
 * signature's evidence (in `client_documents` + `consent_records`) so it is
 * legally reproducible after the fact, even if the wording is updated.
 *
 * **Versioning rule** (CLAUDE.md §12): never edit the text of a published
 * version. To revise the agreement, add a new file `commitment-agreement-v2.ts`
 * and a new `agreement_version` value — old signatures keep pointing at v1.
 */

export const COMMITMENT_AGREEMENT_VERSION = 'commitment-agreement-v1';

export const COMMITMENT_AGREEMENT_DE = `# Heroic Commitment

Mit meiner Unterschrift unten bestätige ich, dass ich H³eroMap™ als das verstehe, was es ist: kein Programm, das mir etwas hinzufügt — sondern ein Spiegel, in dem ich erkenne, wer ich schon bin.

## Worauf ich mich einlasse

- **Ehrlichkeit.** Ich beantworte alle Fragen, Tools und Reflexionen so wahrhaftig wie möglich, auch wenn die Antworten unbequem sind.
- **Tägliche Praxis.** Ich verpflichte mich zur AM- und PM-Bookend-Routine (zusammen weniger als zwei Minuten) für die Dauer des Programms — auch an Tagen, an denen ich keine Lust habe.
- **Wöchentliche Sitzung.** Ich erscheine pünktlich, vorbereitet und mit Fragen, sofern nicht ein wichtiger Grund mich daran hindert. Verschiebungen kündige ich mindestens 24 Stunden vorher an.
- **Phase Gates.** Ich akzeptiere, dass die Phase Gates 1 (nach Schritt 3) und 2 (nach Schritt 6) entscheiden, ob ich weitergehe. Ein „Conditional" bedeutet eine Pufferwoche, kein Versagen. Ein „Fail" heißt: noch nicht — wir vertiefen.
- **Daten.** Ich verstehe, dass meine Coaching-Daten ausschließlich in der EU verarbeitet, niemals an Dritte verkauft und für KI-Insights ausschließlich anonymisiert verwendet werden.

## Was ich von H³eroMap™ erwarten kann

- Einen Coach, der mehr an meine Vision für mich glaubt, als ich an manchen Tagen selbst.
- Werkzeuge, die meine Tugenden, meine Identität und mein Verhalten in Verbindung bringen.
- Klarheit darüber, woran ich gerade arbeite — und warum.
- Eine vollständige Heroic Operating System (HOS)-Dokumentation am Ende meines Programms.

## Mein Heroic Anchor

Mit meiner Unterschrift verpflichte ich mich, das Beste in mir zu erwecken — nicht perfekt, sondern hartnäckig. Wenn ich straucheln werde — und das werde ich — kehre ich zurück. Das ist der Vertrag.

_Datum, Unterschrift, Name in Druckbuchstaben werden beim Klicken auf „Vereinbarung unterzeichnen" elektronisch erfasst._`;

export const COMMITMENT_AGREEMENT_EN = `# Heroic Commitment

By signing below, I acknowledge that H³eroMap™ is not a program that adds something to me — it is a mirror in which I recognize who I already am.

## What I commit to

- **Honesty.** I answer every question, tool, and reflection as truthfully as I can, even when the answers are uncomfortable.
- **Daily practice.** I commit to the AM and PM bookend routines (together less than two minutes) for the duration of the program — including the days I don't feel like it.
- **Weekly session.** I show up on time, prepared, and with questions, unless something significant prevents it. I reschedule with at least 24 hours' notice.
- **Phase Gates.** I accept that Phase Gate 1 (after Step 3) and Phase Gate 2 (after Step 6) decide whether I move forward. A "Conditional" result means a buffer week, not failure. A "Fail" means: not yet — we go deeper.
- **Data.** I understand that my coaching data is processed only in the EU, never sold to third parties, and used for AI insights only after anonymization.

## What I can expect from H³eroMap™

- A coach who believes in my vision for myself more strongly than I do on some days.
- Tools that connect my virtues, my identity, and my behavior.
- Clarity about what I am working on right now — and why.
- A complete Heroic Operating System (HOS) document at the end of my program.

## My Heroic Anchor

By signing, I commit to awakening the best in me — not perfectly, but persistently. When I stumble — and I will — I return. That is the contract.

_Date, signature, and printed name are captured electronically when I click "Sign agreement"._`;

export const COMMITMENT_AGREEMENT_TEXT = {
  de: COMMITMENT_AGREEMENT_DE,
  en: COMMITMENT_AGREEMENT_EN,
} as const;
